/**
 * CalendarPage — month view of everything with a date across the user's
 * courses: quiz openings and deadlines, assignment due dates, and weekly
 * class meetings (with time and location). Works for students and teachers;
 * the backend scopes items to enrollment / taught courses.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import Mascot from '../../components/shared/Mascot';
import { getMyCalendar, CalendarData, CalendarEvent, CourseMeeting } from '../../api/courses';
import { useAuth } from '../../context/AuthContext';

const DAY_KEYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const EVENT_STYLES: Record<CalendarEvent['type'], { chip: string; dot: string; label: string }> = {
  quiz_due: { chip: 'bg-red-50 text-red-700 dark:bg-red-900/25 dark:text-red-300', dot: 'bg-red-500', label: 'Quiz due' },
  assignment_due: { chip: 'bg-amber-50 text-amber-800 dark:bg-amber-900/25 dark:text-amber-300', dot: 'bg-amber-500', label: 'Assignment due' },
  quiz_opens: { chip: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300', dot: 'bg-emerald-500', label: 'Quiz opens' },
};

const fmtTime = (d: Date) =>
  d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

/** "10:00" -> "10:00 AM" (local wall time as entered by the instructor) */
const fmtWallTime = (hhmm: string): string => {
  const [h, m] = hhmm.split(':').map(Number);
  if (isNaN(h)) return hhmm;
  const d = new Date();
  d.setHours(h, m || 0, 0, 0);
  return fmtTime(d);
};

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const CalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const { isTeacher } = useAuth();
  const [data, setData] = useState<CalendarData>({ events: [], meetings: [] });
  const [loading, setLoading] = useState(true);
  const today = useMemo(() => new Date(), []);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDay, setSelectedDay] = useState<Date>(today);

  useEffect(() => {
    getMyCalendar()
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const monthStart = new Date(viewYear, viewMonth, 1);
  const gridStart = new Date(monthStart);
  gridStart.setDate(1 - monthStart.getDay());
  const cells: Date[] = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    return d;
  });

  const eventsOn = (day: Date): CalendarEvent[] =>
    data.events.filter((e) => sameDay(new Date(e.at), day));

  const withinTerm = (m: CourseMeeting, day: Date): boolean => {
    if (m.course_start && day < new Date(m.course_start + 'T00:00')) return false;
    if (m.course_end && day > new Date(m.course_end + 'T23:59')) return false;
    return true;
  };

  const meetingsOn = (day: Date): CourseMeeting[] =>
    data.meetings.filter((m) => m.days.includes(DAY_KEYS[day.getDay()]) && withinTerm(m, day));

  const moveMonth = (delta: number) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const goToCourse = (courseId: string) =>
    navigate(`${isTeacher ? '/teacher' : '/student'}/courses/${courseId}`);

  const selectedEvents = eventsOn(selectedDay);
  const selectedMeetings = meetingsOn(selectedDay);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-3xl">
              Calendar
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Class meetings and deadlines across your courses
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => moveMonth(-1)}
              className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-gray-200 text-gray-500 transition-colors hover:bg-gray-50 dark:border-secondary-700 dark:text-gray-400 dark:hover:bg-secondary-800"
              aria-label="Previous month"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
            </button>
            <span className="min-w-[10rem] text-center text-base font-extrabold tracking-tight text-gray-900 dark:text-white">
              {monthStart.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
            </span>
            <button
              onClick={() => moveMonth(1)}
              className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-gray-200 text-gray-500 transition-colors hover:bg-gray-50 dark:border-secondary-700 dark:text-gray-400 dark:hover:bg-secondary-800"
              aria-label="Next month"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
            </button>
            <button
              onClick={() => { setViewYear(today.getFullYear()); setViewMonth(today.getMonth()); setSelectedDay(today); }}
              className="rounded-full border-2 border-gray-200 px-4 py-1.5 text-sm font-bold text-gray-700 transition-colors hover:bg-gray-50 dark:border-secondary-700 dark:text-gray-200 dark:hover:bg-secondary-800"
            >
              Today
            </button>
          </div>
        </div>

        {loading ? (
          <div className="h-96 animate-pulse rounded-2xl bg-gray-100 dark:bg-secondary-800" />
        ) : (
          <div className="flex flex-col gap-6 xl:flex-row">
            {/* Month grid */}
            <div className="flex-1 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
              <div className="grid grid-cols-7 border-b border-gray-100 dark:border-secondary-700">
                {DAY_KEYS.map((d) => (
                  <div key={d} className="px-2 py-2 text-center text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {d}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {cells.map((day, i) => {
                  const inMonth = day.getMonth() === viewMonth;
                  const isToday = sameDay(day, today);
                  const isSelected = sameDay(day, selectedDay);
                  const dayEvents = eventsOn(day);
                  const dayMeetings = meetingsOn(day);
                  return (
                    <button
                      key={i}
                      onClick={() => setSelectedDay(day)}
                      className={`flex min-h-[5.5rem] flex-col items-stretch gap-1 border-b border-r border-gray-50 p-1.5 text-left align-top transition-colors last:border-r-0 dark:border-secondary-700/50 ${
                        inMonth ? 'bg-white dark:bg-secondary-800' : 'bg-gray-50/60 dark:bg-secondary-900/40'
                      } ${isSelected ? 'ring-2 ring-inset ring-primary-400' : 'hover:bg-primary-50/50 dark:hover:bg-secondary-700/40'}`}
                    >
                      <span
                        className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                          isToday
                            ? 'bg-primary-600 text-gray-900'
                            : inMonth
                              ? 'text-gray-700 dark:text-gray-300'
                              : 'text-gray-300 dark:text-gray-600'
                        }`}
                      >
                        {day.getDate()}
                      </span>
                      <span className="flex flex-col gap-0.5 overflow-hidden">
                        {dayMeetings.slice(0, 2).map((m) => (
                          <span key={m.course_id} className="truncate rounded bg-primary-100 px-1 py-0.5 text-[11px] font-semibold text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                            {m.start ? `${fmtWallTime(m.start)} ` : ''}{m.course_title}
                          </span>
                        ))}
                        {dayEvents.slice(0, 2).map((e) => (
                          <span key={e.id} className={`truncate rounded px-1 py-0.5 text-[11px] font-semibold ${EVENT_STYLES[e.type].chip}`}>
                            {e.title}
                          </span>
                        ))}
                        {dayMeetings.length + dayEvents.length > 4 && (
                          <span className="text-[11px] font-semibold text-gray-500">
                            +{dayMeetings.length + dayEvents.length - 4} more
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Day detail */}
            <div className="w-full space-y-4 xl:w-80">
              <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                <p className="text-sm font-extrabold tracking-tight text-gray-900 dark:text-white">
                  {selectedDay.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
                </p>
                {selectedMeetings.length === 0 && selectedEvents.length === 0 && (
                  <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Nothing scheduled</p>
                )}
                {selectedMeetings.map((m) => (
                  <button
                    key={m.course_id}
                    onClick={() => goToCourse(m.course_id)}
                    className="mt-3 flex w-full items-start gap-3 rounded-xl bg-primary-50 p-3 text-left transition-colors hover:bg-primary-100 dark:bg-primary-900/20 dark:hover:bg-primary-900/30"
                  >
                    <span className="mt-0.5 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-primary-500" />
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-bold text-gray-900 dark:text-white">{m.course_title}</span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400">
                        {m.start && m.end ? `${fmtWallTime(m.start)} to ${fmtWallTime(m.end)}` : m.schedule_text || 'Class meeting'}
                      </span>
                      {m.location && (
                        <span className="mt-0.5 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                          <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
                          {m.location}
                        </span>
                      )}
                    </span>
                  </button>
                ))}
                {selectedEvents.map((e) => (
                  <button
                    key={e.id}
                    onClick={() => goToCourse(e.course_id)}
                    className="mt-3 flex w-full items-start gap-3 rounded-xl bg-gray-50 p-3 text-left transition-colors hover:bg-gray-100 dark:bg-secondary-700/50 dark:hover:bg-secondary-700"
                  >
                    <span className={`mt-0.5 h-2.5 w-2.5 flex-shrink-0 rounded-full ${EVENT_STYLES[e.type].dot}`} />
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-bold text-gray-900 dark:text-white">{e.title}</span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400">
                        {EVENT_STYLES[e.type].label} · {fmtTime(new Date(e.at))} · {e.course_title}
                      </span>
                    </span>
                  </button>
                ))}
              </div>

              {/* Weekly schedule summary */}
              {data.meetings.length > 0 && (
                <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                  <p className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Weekly schedule</p>
                  {data.meetings.map((m) => (
                    <div key={m.course_id} className="mt-3">
                      <p className="text-sm font-bold text-gray-900 dark:text-white">{m.course_title}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {m.days.length > 0 ? m.days.join(', ') : ''}
                        {m.start && m.end ? ` · ${fmtWallTime(m.start)} to ${fmtWallTime(m.end)}` : ''}
                        {!m.days.length && !m.start ? m.schedule_text : ''}
                      </p>
                      {m.location && <p className="text-xs text-gray-500 dark:text-gray-400">{m.location}</p>}
                      {m.course_start && m.course_end && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(m.course_start + 'T00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} to {new Date(m.course_end + 'T00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {data.events.length === 0 && data.meetings.length === 0 && (
                <div className="flex flex-col items-center rounded-2xl border border-dashed border-gray-300 px-6 py-10 text-center dark:border-secondary-700">
                  <Mascot name="clock" chip="#fde047" className="h-14 w-14" blinkDelay={0.5} />
                  <p className="mt-4 text-sm font-semibold text-gray-500 dark:text-gray-400">
                    Nothing on the calendar yet
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Deadlines and class meetings will appear here
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CalendarPage;
